import { getSystemMessageStatus } from "@/shared/utils/systemMessages";
import {
  SystemMessage,
  SystemMessageBody,
  SystemMessagesResponse,
  SystemMessageStatus,
} from "@/shared/utils/interfaces";
import dayjs from "dayjs";

/**
 * Temporary backend for system messages.
 *
 * There is no system messages endpoint yet. The hooks in
 * `src/shared/queries/systemMessages` and `src/shared/mutations/systemMessages`
 * call these functions; once the backend exists, swap each hook's function for
 * the real `get2` / `post2` / `put2` / `destroy2` call (see the TODO in every
 * hook) and delete this file.
 *
 * Unlike the polls mock this one lives in localStorage, so a message an admin
 * creates is still there after a reload and the customer side can be tested.
 * Which messages a user has seen is also kept in localStorage, per user; the
 * backend will keep that itself, the real endpoints won't need the user id.
 */

const STORE_KEY = "mock_system_messages";
const SEEN_KEY = (userId: number) => `mock_system_messages_seen_${userId}`;
const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";

interface Store {
  nextId: number;
  items: SystemMessage[];
}

const delay = <T>(value: T, ms = 300): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

const now = () => dayjs().format(DATE_TIME_FORMAT);
const at = (days: number) =>
  dayjs().add(days, "day").startOf("hour").format(DATE_TIME_FORMAT);

const notFound = () =>
  Promise.reject({ response: { status: 404, data: { error_id: "notFound" } } });

// Poll 1 is the active poll from the polls mock
const seed = (): Store => {
  const items: SystemMessage[] = [
    {
      item_id: 1,
      title: "Help ons het kassasysteem te verbeteren",
      content:
        "<h2>We willen je mening horen</h2>" +
        "<p>De afgelopen weken hebben we hard gewerkt aan het <strong>nieuwe kassasysteem</strong>. " +
        "Nu willen we graag weten hoe het bevalt.</p>" +
        "<ul><li><p>Het duurt minder dan een minuut</p></li><li><p>Je antwoord is anoniem</p></li></ul>" +
        '<div data-type="poll" data-poll-id="1"></div>' +
        "<p>Bedankt voor je hulp! <mark>Team Appsmen</mark></p>",
      start_date: at(-1),
      end_date: at(14),
      created_at: at(-2),
      updated_at: at(-2),
    },
    {
      item_id: 2,
      title: "Gepland onderhoud",
      content:
        "<p>Op <strong>zondagnacht</strong> voeren we onderhoud uit tussen 02:00 en 04:00. " +
        "Het CMS is dan kort niet bereikbaar.</p>",
      start_date: at(3),
      end_date: at(6),
      created_at: at(-1),
      updated_at: at(-1),
    },
    {
      item_id: 3,
      title: "Nieuwe bezorgapp beschikbaar",
      content:
        '<p>De nieuwe bezorgapp staat in de <a href="https://appsmen.nl">App Store en Play Store</a>.</p>',
      start_date: at(-30),
      end_date: at(-20),
      created_at: at(-31),
      updated_at: at(-31),
    },
  ];
  return { nextId: items.length + 1, items };
};

const read = <T>(key: string, fallback: () => T): T => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as T;
  } catch {
    // Broken or unavailable storage falls back to the default
  }
  return fallback();
};

const write = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked, the mock keeps working for this call
  }
};

const readStore = (): Store => {
  const store = read<Store | null>(STORE_KEY, () => null);
  if (store && Array.isArray(store.items)) return store;
  const seeded = seed();
  write(STORE_KEY, seeded);
  return seeded;
};

const readSeen = (userId: number): number[] =>
  read<number[]>(SEEN_KEY(userId), () => []);

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

interface GetSystemMessagesParams {
  search?: string;
  status?: SystemMessageStatus;
  limit: number;
  offset: number;
}

export const getMockSystemMessages = ({
  search,
  status,
  limit,
  offset,
}: GetSystemMessagesParams): Promise<SystemMessagesResponse> => {
  const term = search?.trim().toLowerCase();
  const filtered = readStore()
    .items.filter(
      (message) => !term || message.title.toLowerCase().includes(term)
    )
    .filter(
      (message) => !status || getSystemMessageStatus(message) === status
    )
    // Newest start first
    .sort(
      (a, b) => dayjs(b.start_date).valueOf() - dayjs(a.start_date).valueOf()
    );

  return delay({
    total: filtered.length,
    limit,
    offset,
    items: filtered.slice(offset, offset + limit),
  });
};

export const getMockSystemMessage = (id: number): Promise<SystemMessage> => {
  const message = readStore().items.find((m) => m.item_id === id);
  return message ? delay(message) : notFound();
};

export const createMockSystemMessage = (
  body: SystemMessageBody
): Promise<SystemMessage> => {
  const store = readStore();
  const message: SystemMessage = {
    ...body,
    item_id: store.nextId,
    created_at: now(),
    updated_at: now(),
  };
  write(STORE_KEY, {
    nextId: store.nextId + 1,
    items: [...store.items, message],
  });
  return delay(message);
};

export const updateMockSystemMessage = (
  id: number,
  body: SystemMessageBody
): Promise<SystemMessage> => {
  const store = readStore();
  const current = store.items.find((m) => m.item_id === id);
  if (!current) return notFound();

  const message: SystemMessage = { ...current, ...body, updated_at: now() };
  write(STORE_KEY, {
    ...store,
    items: store.items.map((m) => (m.item_id === id ? message : m)),
  });
  return delay(message);
};

export const deleteMockSystemMessage = (id: number): Promise<void> => {
  const store = readStore();
  if (!store.items.some((m) => m.item_id === id)) return notFound();
  write(STORE_KEY, {
    ...store,
    items: store.items.filter((m) => m.item_id !== id),
  });
  return delay(undefined);
};

// ---------------------------------------------------------------------------
// Customer
// ---------------------------------------------------------------------------

// Active messages the user has not seen yet, oldest first so they read in order
export const getMockPendingSystemMessages = (
  userId: number
): Promise<SystemMessage[]> => {
  const seen = new Set(readSeen(userId));
  const pending = readStore()
    .items.filter((message) => !seen.has(message.item_id))
    .filter(
      (message) =>
        getSystemMessageStatus(message) === SystemMessageStatus.ACTIVE
    )
    .sort(
      (a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf()
    );
  return delay(pending);
};

export const markMockSystemMessageSeen = (
  userId: number,
  id: number
): Promise<void> => {
  const seen = readSeen(userId);
  if (!seen.includes(id)) write(SEEN_KEY(userId), [...seen, id]);
  return delay(undefined, 100);
};
